import { request } from './client'

/* ============================================================================
   W-125 / W-126 — AUTHORING WHAT A CLEAN CONSISTS OF.

   **A PUBLISHED VERSION IS NEVER EDITED**, and there is deliberately no call in this file
   that could edit one. A draft is saved; publishing stamps the draft. Every completed
   clean records which version it followed, so editing one in place would silently rewrite
   the history of every inspection that used it.

   THE DIFF AND THE PHOTOGRAPH COUNT COME FROM THE SERVER. They are rules about what a
   change costs the hotel, and a second implementation here would be a second answer —
   which on this screen means two different numbers for "how many more photographs a day",
   one of which somebody decides on.
   ========================================================================= */

/** What a draft changes about the version in use. The server's word. */
export type ChecklistChange =
  | 'ADDED' | 'REMOVED' | 'RELABELLED'
  | 'BECAME_REQUIRED' | 'BECAME_OPTIONAL'
  | 'PHOTO_NOW_REQUIRED' | 'PHOTO_NO_LONGER_REQUIRED'
  | 'MOVED'

export interface ChecklistTemplateRow {
  templateId: string
  /** The TASK TYPE code — how a template is chosen for a task. */
  code: string
  name: string
  /** Null until somebody types one. Every screen falls back to the English. */
  nameAr: string | null
  appliesTo: string | null
  appliesToAr: string | null
  /** Null when nothing has ever been published: a template that exists and has never
      been in use, which is exactly what the day somebody creates one looks like. */
  liveVersion: number | null
  liveItems: number
  /** From the TASK TYPE, not a copy on the template — one place for what a clean takes.
   *
   *  **THERE WAS A `credits` FIELD HERE AND IT WAS A LIE OF OMISSION.** It came from a
   *  `TaskType.Credits` column that priced a kind of work flatly, the same for a twin and a
   *  suite, while the rooms said the two take different lengths of time (defect #111). What
   *  a clean costs is not a property of the KIND of work — it is the room's minutes scaled
   *  by this ratio, which is a different number per room and therefore not something a list
   *  of templates can honestly show a column for. So the column went, and this screen shows
   *  the number that is actually true of a task type: how long the work takes as a standard. */
  standardMinutes: number
  inUseSinceUtc: string | null
  /** What publishing would make it, so the button can say "publish as v8". */
  draftVersion: number | null
  retiredVersions: number
  isActive: boolean
}

export interface ChecklistTemplateBoard {
  templates: ChecklistTemplateRow[]
  live: number
  drafts: number
  retired: number
}

export interface ChecklistItemDraft {
  /** A KEY, never translated — BATH_TOWELS_REPLACED. A report counting how often
      bathroom towels were missed has to count one thing, not two strings. */
  code: string
  labelEn: string
  labelAr: string
  isMandatory: boolean
  requiresPhoto: boolean
}

export interface ChecklistDifference { code: string; change: ChecklistChange }

export interface ChecklistDraftView {
  templateId: string
  code: string
  name: string
  nameAr: string | null
  draftVersionId: string | null
  draftVersionNumber: number
  liveVersionNumber: number | null
  items: ChecklistItemDraft[]
  liveItems: ChecklistItemDraft[]
  changes: ChecklistDifference[]
  /**
   * What this draft costs, in photographs a day, measured from what the hotel has
   * actually been cleaning. Negative is a saving.
   *
   * **NULL IS NOT ZERO.** Null means there is nothing to measure yet — before go-live —
   * and "0 more photographs a day" would read as "this change is free".
   */
  extraPhotographsPerDay: number | null
  cleansPerDay: number
  canPublish: boolean
}

export const fetchChecklists = (propertyId: string, signal?: AbortSignal) =>
  request<ChecklistTemplateBoard>(`/properties/${propertyId}/checklists`, signal ? { signal } : {})

/** Opens the draft, creating one from the live version if none is open. */
export const openChecklistDraft = (propertyId: string, templateId: string) =>
  request<ChecklistDraftView>(
    `/properties/${propertyId}/checklists/${templateId}/draft`, { method: 'POST' })

export const saveChecklistDraft = (
  propertyId: string, templateId: string, items: ChecklistItemDraft[],
) => request<ChecklistDraftView>(
  `/properties/${propertyId}/checklists/${templateId}/draft`,
  { method: 'PUT', body: { items } })

export const discardChecklistDraft = (propertyId: string, templateId: string) =>
  request<void>(`/properties/${propertyId}/checklists/${templateId}/draft`, { method: 'DELETE' })

/** Its own noun, not a flag on the save — publishing is a decision with a consequence
    for every clean from now on. */
export const publishChecklistDraft = (propertyId: string, templateId: string) =>
  request<ChecklistDraftView>(
    `/properties/${propertyId}/checklists/${templateId}/publication`, { method: 'POST' })

export const createChecklistTemplate = (
  propertyId: string, taskTypeCode: string, name?: string, nameAr?: string,
) => request<ChecklistTemplateRow>(`/properties/${propertyId}/checklists`, {
  method: 'POST',
  body: {
    taskTypeCode,
    ...(name === undefined ? {} : { name }),
    ...(nameAr === undefined ? {} : { nameAr }),
  },
})

/**
 * RENAMING A TEMPLATE, IN EITHER LANGUAGE — and it makes NO VERSION.
 *
 * A version exists because what a clean CONSISTS OF changed; a name is not part of that.
 * Versioning a rename would fill the history with entries that say nothing and hand every
 * handset in the hotel a "new" checklist for a change nobody made.
 *
 * Omitting a field leaves it alone. An EMPTY `nameAr` clears it back to the English
 * fallback, which is a real intention — "I typed the wrong thing". The English name may
 * not be emptied: it is what every other screen falls back to.
 */
export const renameChecklistTemplate = (
  propertyId: string, templateId: string, change: { name?: string; nameAr?: string },
) => request<ChecklistTemplateRow>(
  `/properties/${propertyId}/checklists/${templateId}/name`, { method: 'POST', body: change })
